import React, { useCallback, useEffect, useState } from 'react';
import {
  X, Loader2, Play, Trash2, BookMarked, Check, AlertTriangle, ChevronRight, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  applyRecipe, deleteRecipe, getRecipe, getRecipes, getSessionSteps, saveRecipe,
} from '../utils/api';
import { useT } from '../i18n';

/**
 * Recipes: what was done to a file, kept so it can be replayed on the next one.
 *
 * The panel exists mostly to show what *cannot* be replayed before anything is.
 * A recipe applied blind on a file whose columns have moved would either fail
 * halfway or, worse, half-succeed; the preflight turns that into a list the
 * user reads, with the choice of replaying only the steps that hold.
 */
const Recipes = ({ onClose, onRecipeApplied, dataLoaded }) => {
  const t = useT();
  const [recipes, setRecipes] = useState([]);
  const [steps, setSteps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const listed = await getRecipes();
      setRecipes(listed.recipes || []);
      if (dataLoaded) {
        const current = await getSessionSteps();
        setSteps(current.steps || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipes.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [dataLoaded, t]);

  useEffect(() => { load(); }, [load]);

  const openRecipe = async (recipeId) => {
    setSelected(recipeId);
    setDetail(null);
    try {
      setDetail(await getRecipe(recipeId));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipes.loadFailed'));
      setSelected(null);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('recipes.nameRequired'));
      return;
    }
    try {
      setSaving(true);
      const result = await saveRecipe(name.trim());
      toast.success(t('recipes.saved', {
        name: result.recipe.name,
        count: result.recipe.step_count,
      }));
      setName('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipes.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async (upTo = null) => {
    try {
      setApplying(true);
      const result = await applyRecipe(selected, upTo);
      onRecipeApplied({
        ...result,
        localizedMessage: t('recipes.applied', {
          name: detail.recipe.name,
          count: result.applied.length,
        }),
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipes.applyFailed'), { duration: 9000 });
    } finally {
      setApplying(false);
    }
  };

  const handleDelete = async (recipeId, recipeName) => {
    if (!window.confirm(t('recipes.confirmDelete', { name: recipeName }))) return;
    try {
      await deleteRecipe(recipeId);
      if (selected === recipeId) { setSelected(null); setDetail(null); }
      await load();
      toast.success(t('recipes.deleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipes.deleteFailed'));
    }
  };

  const checks = detail?.preflight || null;
  const firstBlocked = checks ? checks.findIndex((entry) => !entry.replayable) : -1;
  const runnable = firstBlocked === -1 ? (checks?.length ?? 0) : firstBlocked;

  // Written out rather than looked up in a map: the keys have to appear
  // literally inside t(...) for tests/test_i18n_sprint9.py to see them, and a
  // key nothing appears to reference is exactly what it deletes.
  const reasonText = (entry) => {
    switch (entry.reason) {
      case 'needs_second_file':
        return t('recipes.reason.secondFile');
      case 'missing_columns':
        return t('recipes.reason.missingColumns', {
          columns: (entry.missing_columns || []).join(', '),
        });
      case 'unknown_operation':
        return t('recipes.reason.unknown');
      case 'arguments_not_kept':
        return t('recipes.reason.oversized');
      case 'after_blocked_step':
        return t('recipes.reason.afterBlocked');
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="recipes-modal">
        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookMarked className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">{t('recipes.title')}</h2>
            </div>
            <button onClick={onClose} className="btn-ghost h-8 w-8 p-0" data-testid="recipes-close"
                    aria-label={t('operations.close')}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-muted-foreground">{t('recipes.intro')}</p>

          {/* Save what has been done to the current file */}
          {dataLoaded && (
            <div className="card p-4 space-y-2">
              <p className="text-sm font-medium">
                {t('recipes.currentSteps', { count: steps.length })}
              </p>
              {steps.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('recipes.noStepsYet')}</p>
              ) : (
                <>
                  <ol className="text-xs text-muted-foreground space-y-0.5 max-h-28 overflow-y-auto">
                    {steps.map((entry) => (
                      <li key={entry.index} className="flex items-start gap-1.5">
                        <span className="text-muted-foreground/60">{entry.index + 1}.</span>
                        <span className="flex-1">{entry.label}</span>
                        {!entry.replayable && (
                          <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                        )}
                      </li>
                    ))}
                  </ol>
                  <div className="flex gap-2 pt-1">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="input h-9"
                      placeholder={t('recipes.namePlaceholder')}
                      data-testid="recipe-name"
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn-primary h-9 px-4 flex-shrink-0 flex items-center gap-2"
                      data-testid="recipe-save"
                    >
                      {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t('recipes.save')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* The shelf */}
          <div>
            <h3 className="text-sm font-medium mb-2">{t('recipes.shelfTitle')}</h3>
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : recipes.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">{t('recipes.empty')}</p>
            ) : (
              <div className="border border-border rounded-[var(--radius)] divide-y divide-border">
                {recipes.map((recipe) => (
                  <div key={recipe.recipe_id}>
                    <div className="flex items-center gap-3 p-3">
                      <button
                        onClick={() => (selected === recipe.recipe_id
                          ? setSelected(null)
                          : openRecipe(recipe.recipe_id))}
                        className="flex-1 text-left min-w-0"
                        data-testid="recipe-row"
                        data-recipe-id={recipe.recipe_id}
                      >
                        <p className="text-sm font-medium truncate">{recipe.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2">
                          <span>{t('recipes.stepCount', { count: recipe.step_count })}</span>
                          {recipe.source && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <Clock className="h-3 w-3" />{recipe.source}
                            </span>
                          )}
                        </p>
                      </button>
                      <ChevronRight
                        className={`h-4 w-4 text-muted-foreground transition-transform ${
                          selected === recipe.recipe_id ? 'rotate-90' : ''
                        }`}
                      />
                      <button
                        onClick={() => handleDelete(recipe.recipe_id, recipe.name)}
                        className="btn-ghost h-8 w-8 p-0 text-destructive"
                        aria-label={t('recipes.delete')}
                        data-testid={`recipe-delete-${recipe.recipe_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {selected === recipe.recipe_id && (
                      <div className="px-3 pb-3 space-y-3" data-testid="recipe-detail">
                        {!detail ? (
                          <div className="flex justify-center py-3">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : (
                          <>
                            <ol className="text-xs space-y-1">
                              {detail.steps.map((entry, position) => {
                                const check = checks ? checks[position] : null;
                                const ok = check ? check.replayable : entry.replayable;
                                return (
                                  <li key={entry.index} className="flex items-start gap-2">
                                    {ok ? (
                                      <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                    ) : (
                                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                      <p className={ok ? '' : 'text-muted-foreground'}>{entry.label}</p>
                                      {check && !ok && reasonText(check) && (
                                        <p className="text-amber-700">{reasonText(check)}</p>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>

                            {!dataLoaded ? (
                              <p className="text-xs text-muted-foreground">{t('recipes.needFile')}</p>
                            ) : !checks ? null : firstBlocked === -1 ? (
                              <button
                                onClick={() => handleApply(null)}
                                disabled={applying}
                                className="btn-primary h-9 px-4 flex items-center gap-2"
                                data-testid="recipe-apply"
                              >
                                {applying ? <Loader2 className="h-4 w-4 animate-spin" />
                                          : <Play className="h-4 w-4" />}
                                {t('recipes.apply')}
                              </button>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-xs text-amber-700">
                                  {t('recipes.blockedAt', { step: firstBlocked + 1 })}
                                </p>
                                {runnable > 0 && (
                                  <button
                                    onClick={() => handleApply(runnable)}
                                    disabled={applying}
                                    className="btn-secondary h-9 px-4 flex items-center gap-2"
                                    data-testid="recipe-apply-partial"
                                  >
                                    {applying ? <Loader2 className="h-4 w-4 animate-spin" />
                                              : <Play className="h-4 w-4" />}
                                    {t('recipes.applyUpTo', { count: runnable })}
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Recipes;
